'use client';

import type React from 'react';
import { useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import InviteFriends from './components/InviteFriends';
import MobileInvite from './components/MobileInvite';
import RecommendedUsers from './components/RecommendedUsers';

import { Layout } from '@/components/GenericsTournaments';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import { GDTApiErrors } from '@/types';
import { AdsSections } from '@/types/ads';
import type {
  InviteUserByIdResponse,
  ResponseInvitarAmigo,
  ResponseSugerirUsuariosGCA,
  TournamentInviteFormData,
} from '@/types/invites';
import { TEXTS } from '@/utils/constants';
import { ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS } from '@/utils/regex';

export enum CompetitionType {
  TOURNAMENT = 'tournament',
  CUP = 'cup',
  MINILEAGUES = 'minileagues',
}

export interface InviteServices {
  inviteByEmail: (
    data: TournamentInviteFormData
  ) => Promise<
    | Promise<ResponseInvitarAmigo | GDTApiErrors>
    | ResponseInvitarAmigo
    | GDTApiErrors
  >;

  inviteById: (
    idTorneo: number,
    idUsuarioDT: number
  ) => Promise<
    | Promise<InviteUserByIdResponse | GDTApiErrors>
    | InviteUserByIdResponse
    | GDTApiErrors
  >;

  suggestUsers: (
    cantMaxResultados?: number
  ) => Promise<
    | Promise<ResponseSugerirUsuariosGCA | GDTApiErrors>
    | ResponseSugerirUsuariosGCA
    | GDTApiErrors
  >;

  // Nuevo servicio para buscar DTs por nombre
  searchByName: (searchTerm: string, idTorneo: number) => Promise<any>;
}

interface GenericInviteFriendsProps {
  services: InviteServices | any;
  redirectRoute: () => void;
  type: 'tournament' | 'cup' | 'minileagues' | string;
  competitionId: number;
  adsSections?: AdsSections;
}

export default function InviteFriendsPage({
  services,
  redirectRoute,
  type,
  competitionId,
  adsSections,
}: Readonly<GenericInviteFriendsProps>) {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('name');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const location = useLocation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const { isDesktop } = useIsDesktop();

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(
        ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS,
        TEXTS.tda.invalidCharacterDT
      )
      .required('Debés ingresar un nombre'),
    email: Yup.string()
      .email('El e-mail debe contener @ y dominio.')
      .required('Debés ingresar un e-mail'),
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormData((prevData) => ({ ...prevData, [name]: value }));

    validationSchema
      .validateAt(name, { [name]: value })
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
      })
      .catch((err) => {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: err.message }));
      });
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    validationSchema
      .validateAt(name, { [name]: value })
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
      })
      .catch((err) => {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: err.message }));
      });
  };

  const handleInviteByEmail = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      setIsLoading(true);

      // Crear el objeto base
      const baseData = {
        names: [formData.name],
        emails: [formData.email],
      };

      // Crear el objeto final según el tipo usando aserción de tipo
      let inviteData: any;

      if (type === 'tournament') {
        inviteData = {
          ...baseData,
          idTDA: competitionId,
        };
      } else if (type === 'cup') {
        inviteData = {
          ...baseData,
          idGrupo: competitionId,
        };
      } else if (type === CompetitionType.MINILEAGUES) {
        inviteData = {
          ...baseData,
          idMiniLiga: competitionId,
        };
      } else {
        inviteData = {
          ...baseData,
          competitionId,
        };
      }

      // Usar aserción de tipo para evitar errores de TypeScript
      const response = await services.inviteByEmail(
        inviteData as TournamentInviteFormData
      );

      // Verificar si la respuesta tiene la propiedad 'estado'
      let isSuccess = false;

      if (response && typeof response === 'object') {
        // Verificar directamente si 'estado' existe y es true
        if ('estado' in response) {
          isSuccess = Boolean(response.estado);
        }
      }

      const message = response.mensaje ?? 'Tu invitación por mail fue enviada.';

      // Mostrar mensaje
      toast.show(message);

      // Si fue exitoso, limpiar el formulario
      if (isSuccess) {
        setFormData({ name: '', email: '' });
        setErrors({});
      }
    } catch (error: any) {
      // Manejar errores de validación
      if (error.inner) {
        const newErrors: { name?: string; email?: string } = {};
        error.inner.forEach((validationError: Yup.ValidationError) => {
          newErrors[validationError.path as keyof typeof formData] =
            validationError.message;
        });
        setErrors(newErrors);
        toast.show('Por favor corrige los errores antes de enviar.');
      } else {
        // Otros errores
        toast.show(
          'Ocurrió un error al enviar la invitación. Intenta nuevamente.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteUserById = async (
    userId: number
  ): Promise<boolean | undefined> => {
    let exitoso = false;
    if (!competitionId) {
      toast.show('No se pudo obtener el ID del torneo.');
      return;
    }

    try {
      setIsLoading(true);
      const response = await services.inviteById(competitionId, userId);

      if ('estado' in response && response.estado) {
        toast.show('La invitación fue enviada exitosamente.');
        exitoso = true;
      } else if ('invPendiente' in response && response.invPendiente) {
        toast.show('Ya existe una invitación pendiente para este usuario.');
        exitoso = true;
      } else {
        toast.show(
          response.mensaje ?? 'Hubo un problema al enviar la invitación.'
        );
      }
    } catch (error) {
      toast.show(
        'Ocurrió un error al enviar la invitación. Por favor, intenta de nuevo.'
      );
    } finally {
      setIsLoading(false);
    }
    return exitoso;
  };

  if (!isDesktop) {
    return (
      <MobileInvite
        activeTab={activeTab}
        errors={errors}
        formData={formData}
        idTorneo={competitionId}
        isDesktop={isDesktop}
        isLoading={isLoading}
        services={services}
        setActiveTab={setActiveTab}
        type={type}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
        onInviteById={handleInviteUserById}
        onNavigateToTourney={redirectRoute}
        onSubmit={handleInviteByEmail}
      />
    );
  }

  return (
    <Layout
      withBanners
      adsSections={adsSections}
      main={() => (
        <div className="max-w-[674px] pr-10 mt-5">
          <InviteFriends
            activeTab={activeTab}
            errors={errors}
            formData={formData}
            idTorneo={competitionId}
            isDesktop={isDesktop}
            services={services}
            setActiveTab={setActiveTab}
            setShowSuccess={setShowSuccess}
            showSuccess={showSuccess}
            type={type}
            onInputBlur={handleInputBlur}
            onInputChange={handleInputChange}
            onInviteById={handleInviteUserById}
            onNavigateToTourney={redirectRoute}
            onSubmit={handleInviteByEmail}
          />
        </div>
      )}
      sidebar={() => (
        <div className="max-w-[500px]">
          <RecommendedUsers
            key={services.suggestUsers.length}
            idTorneo={competitionId}
            suggestUsersService={services.suggestUsers}
            type={type}
            onInviteById={handleInviteUserById}
          />
        </div>
      )}
    />
  );
}
